/**
 * Pattern: sequences you watch grow.
 *
 * Each term is drawn as the dots that make it, arranged the way that explains
 * it — squares as squares, triangles as triangles with one more row each time.
 * A child taps to grow the next one, and the gap between the terms is written
 * underneath, because for triangle numbers the gaps are 2, 3, 4, 5 and that is
 * a nicer discovery than the sequence itself.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, Plus, RotateCcw } from 'lucide-preact';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play, playNote } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import type { RuleId } from '../../lib/sequences.ts';
import { dotsFor, extentOf, gapsOf, isSteady, RULES, termsOf } from '../../lib/sequences.ts';
import { groupDigits } from '../../lib/bignum.ts';
import { Chip, Coach, Stepper, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const TOP = 152;
const BOTTOM = 82;
const MAX_TERMS = 9;

const TERM_COLORS = ['--crayon-teal', '--crayon-sky', '--crayon-purple', '--crayon-orange', '--crayon-red', '--crayon-green'];

export function SequenceToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const clockRef = useRef(0);
  /** When each term arrived, so a new one can grow in rather than appear. */
  const bornRef = useRef<number[]>([]);

  const [rule, setRule] = useState<RuleId>('triangle');
  const [shown, setShown] = useState(4);
  const [step, setStep] = useState(2);

  const options = { start: 1, step };
  const terms = termsOf(rule, shown, options);
  const gaps = gapsOf(terms);
  const steady = isSteady(terms);

  const live = useRef({ rule, shown, step });
  live.current = { rule, shown, step };

  const grow = () => {
    if (live.current.shown >= MAX_TERMS) return;
    const next = live.current.shown + 1;
    bornRef.current[next - 1] = clockRef.current;
    setShown(next);
    playNote(Math.min(12, next + 2), { duration: 0.3, gain: 0.2 });
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) spawnSparks(sparksRef.current, { x: rect.width * 0.8, y: rect.height * 0.55 }, partyColors(), 10, 260);
  };

  const restart = (nextRule = rule, nextStep = step) => {
    setRule(nextRule);
    setStep(nextStep);
    setShown(4);
    bornRef.current = [];
    clockRef.current = 0;
    play('whoosh', 0.6);
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      clockRef.current += dt;
      updateSparks(sparksRef.current, dt);
      const { rule: r, shown: count, step: s } = live.current;
      const opts = { start: 1, step: s };

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // Lay the terms across the paper, sized so the biggest one still fits.
      const values = termsOf(r, count, opts);
      let widest = 1;
      let tallest = 1;
      for (let n = 1; n <= count; n++) {
        const e = extentOf(r, n, opts);
        widest = Math.max(widest, e.w);
        tallest = Math.max(tallest, e.h);
      }

      const padX = 16;
      const gap = 12;
      const slot = (width - padX * 2 - gap * (count - 1)) / count;
      const usableH = height - TOP - BOTTOM;
      const cell = Math.max(2, Math.min(slot / widest, usableH / tallest, 22));

      for (let n = 1; n <= count; n++) {
        const dots = dotsFor(r, n, opts);
        const e = extentOf(r, n, opts);
        const originX = padX + (n - 1) * (slot + gap) + (slot - e.w * cell) / 2;
        // Sit each arrangement on a common floor, so growth reads upward.
        const floorY = TOP + usableH * 0.82;
        const originY = floorY - e.h * cell;

        // New terms grow in rather than blinking into existence.
        const born = bornRef.current[n - 1] ?? 0;
        const age = Math.min(1, (clockRef.current - born) / 0.45);
        const grown = born === 0 ? 1 : age * age * (3 - 2 * age);

        const color = token(TERM_COLORS[(n - 1) % TERM_COLORS.length]);
        const radius = cell * 0.36 * grown;

        for (const dot of dots) {
          ctx.beginPath();
          ctx.arc(originX + dot.x * cell + cell / 2, originY + dot.y * cell + cell / 2, Math.max(1, radius), 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
          if (cell > 9) {
            ctx.lineWidth = Math.max(1, cell * 0.07);
            ctx.strokeStyle = 'rgba(44, 37, 69, 0.7)';
            ctx.stroke();
          }
        }

        // The number under its own picture.
        ctx.save();
        ctx.textAlign = 'center';
        ctx.fillStyle = token('--ink');
        ctx.font = `800 ${Math.min(20, slot * 0.42)}px ui-rounded, system-ui, sans-serif`;
        ctx.globalAlpha = grown;
        ctx.fillText(groupDigits(BigInt(values[n - 1])), padX + (n - 1) * (slot + gap) + slot / 2, floorY + 28);
        ctx.restore();

        // The step it took to get here, written in the space between.
        if (n > 1) {
          ctx.save();
          ctx.textAlign = 'center';
          ctx.fillStyle = token('--crayon-green');
          ctx.font = `700 ${Math.min(14, slot * 0.3)}px ui-rounded, system-ui, sans-serif`;
          ctx.globalAlpha = grown * 0.95;
          const midX = padX + (n - 1) * (slot + gap) - gap / 2;
          ctx.fillText(`+${values[n - 1] - values[n - 2]}`, midX, floorY + 52);
          ctx.restore();
        }
      }

      drawSparks(ctx, sparksRef.current);
    },

    onDown: () => grow(),
  });

  const ruleLine = RULES.find((r) => r.id === rule)?.line ?? '';
  const canStep = rule === 'add' || rule === 'times';

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Growing number patterns" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label="Grow the next one" hideLabel disabled={shown >= MAX_TERMS} onClick={grow}>
              <Plus size={24} strokeWidth={2.75} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel onClick={() => restart()}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>{terms.slice(-5).join(', ')}</strong>
          <span>{ruleLine}</span>
        </p>

        <Coach>
          {gaps.length > 1 && !steady
            ? `the steps go up: ${gaps.slice(-4).join(', ')}`
            : 'tap the paper to grow the next one'}
        </Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="Which pattern">
          {RULES.map((r) => (
            <Chip key={r.id} label={r.label} pressed={rule === r.id} onClick={() => restart(r.id, step)} />
          ))}
        </div>
        {canStep && (
          <div class="dock__row">
            <Stepper
              value={step}
              min={2}
              max={12}
              label={rule === 'add' ? 'to add' : 'to times by'}
              onChange={(n) => restart(rule, n)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
